
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
    getLoggedInUser,
    isUserLoggedIn
} from "../../pages/userapp/ServiceUserApp";

import {
    getForumTopicAllPage,
    deleteForumTopic
} from "../../pages/forum/forumTopicService";

import ConfirmationModal from "../../components/home/ConfirmationModal";

import ForumSearchBar from "./ForumSearchBar";
import ForumTopicList from "./ForumTopicList";
import ForumTopicForm from "./ForumTopicForm";
import ForumTopicDetail from "./ForumTopicDetail";

import Pagination from "../../common/Pagination";
import "./Forum.css";

export default function ForumPage() {

    const { t } = useTranslation();
    const navigate = useNavigate();

    // ============================================================
    // Utilisateur connecté
    // ============================================================
    const connected = isUserLoggedIn();
    const loggedUser = getLoggedInUser();

    // ============================================================
    // Etats
    // ============================================================

    const [topics, setTopics] = useState([]);

    const [loading, setLoading] = useState(false);

    const [keyword, setKeyword] = useState("");

    const [page, setPage] = useState(0);

    const [size] = useState(10);

    const [totalPages, setTotalPages] = useState(0);

    const [selectedTopic, setSelectedTopic] =
        useState(null);

    const [showForm, setShowForm] =
        useState(false);

    const [showDetail, setShowDetail] =
        useState(false);

    // ============================================================
    // Suppression
    // ============================================================

    const [deleteTopicId, setDeleteTopicId] =
        useState(null);

    const [showConfirmationModal, setShowConfirmationModal] =
        useState(false);

    // ============================================================
    // Chargement des sujets
    // ============================================================

    const loadTopics = async () => {

        setLoading(true);

        try {

            const response = await getForumTopicAllPage(
                keyword,
                page,
                size,
                ["dateForumTopic"],
                "DESC"
            );

            setTopics(
                response.data.content
            );

            setTotalPages(
                response.data.totalPages
            );

        }
        catch (error) {

            console.error(
                "Erreur lors du chargement des sujets :",
                error
            );

        }
        finally {

            setLoading(false);
        }
    };

    // ============================================================
    // Chargement initial / recherche / pagination
    // ============================================================

    useEffect(() => {

        loadTopics();

    }, [
        keyword,
        page
    ]);

    // ============================================================
    // Redirection login
    // ============================================================

    const redirectToLogin = () => {

        navigate("/login");
    };

    // ============================================================
    // Ajouter un sujet
    // ============================================================

    const handleAdd = () => {

        if (!connected) {

            redirectToLogin();

            return;
        }

        setSelectedTopic(null);

        setShowForm(true);
    };

    // ============================================================
    // Modifier un sujet
    // ============================================================

    const handleEdit = (topic) => {

        if (!connected) {

            redirectToLogin();

            return;
        }

        setSelectedTopic(topic);

        setShowForm(true);
    };

    // ============================================================
    // Afficher le détail
    // ============================================================

    const handleDetail = (topic) => {

        setSelectedTopic(topic);

        setShowDetail(true);
    };

    // ============================================================
    // Sélection du sujet à supprimer
    // ============================================================

    const selectId = (id) => {

        // --------------------------------------------------------
        // Utilisateur non connecté
        // --------------------------------------------------------

        if (!connected) {

            redirectToLogin();

            return;
        }

        // --------------------------------------------------------
        // Vérification ID
        // --------------------------------------------------------

        if (!id) {

            console.error(
                "Impossible de supprimer le sujet : ID absent."
            );

            return;
        }

        // --------------------------------------------------------
        // Mémoriser l'ID
        // --------------------------------------------------------

        setDeleteTopicId(id);

        // --------------------------------------------------------
        // Afficher le modal
        // --------------------------------------------------------

        setShowConfirmationModal(true);
    };

    // ============================================================
    // Fermer le modal
    // ============================================================

    const handleCloseModal = () => {

        setShowConfirmationModal(false);

        setDeleteTopicId(null);
    };

    // ============================================================
    // Suppression définitive après confirmation
    // ============================================================

    const handleConfirmDelete = async () => {

        // --------------------------------------------------------
        // Vérification ID
        // --------------------------------------------------------

        if (!deleteTopicId) {

            console.error(
                "Impossible de supprimer le sujet : ID absent."
            );

            handleCloseModal();

            return;
        }

        // --------------------------------------------------------
        // Vérification connexion
        // --------------------------------------------------------

        if (!connected) {

            handleCloseModal();

            redirectToLogin();

            return;
        }

        try {

            // ----------------------------------------------------
            // Suppression backend
            // ----------------------------------------------------

            await deleteForumTopic(
                deleteTopicId
            );

            // ----------------------------------------------------
            // Fermer le modal
            // ----------------------------------------------------

            setShowConfirmationModal(false);

            setDeleteTopicId(null);

            // ----------------------------------------------------
            // Recharger la liste
            // ----------------------------------------------------

            await loadTopics();

        }
        catch (error) {

            console.error(
                "Erreur lors de la suppression du sujet :",
                error
            );

            // ----------------------------------------------------
            // Fermer le modal même en cas d'erreur
            // ----------------------------------------------------

            setShowConfirmationModal(false);

            setDeleteTopicId(null);
        }
    };

    // ============================================================
    // Sujet enregistré
    // ============================================================

    const handleSaved = () => {

        setShowForm(false);

        loadTopics();
    };

    // ============================================================
    // RENDER
    // ============================================================

    return (

        <div className="container-fluid forum-page">

            <div className="card shadow">

                {/* ==================================================
                    HEADER
                ================================================== */}

                <div className="card-header bg-success text-white">

                    <div
                        className="
                            d-flex
                            justify-content-between
                            align-items-center
                        "
                    >

                        <div
                            className="
                                d-flex
                                align-items-center
                            "
                        >

                            {/* --------------------------------------
                                ACCUEIL
                            -------------------------------------- */}

                            <button
                                className="
                                    btn
                                    btn-light
                                    me-3
                                "
                                onClick={() =>
                                    navigate("/")
                                }
                            >

                                <i className="bi bi-house-door-fill me-2"></i>

                                {t("forum.home")}

                            </button>

                            {/* --------------------------------------
                                TITRE
                            -------------------------------------- */}

                            <h4 className="mb-0">

                                <i className="bi bi-chat-square-text-fill me-2"></i>

                                {t("forum.title")}

                            </h4>

                        </div>

                        {/* ------------------------------------------
                            NOUVEAU SUJET
                        ------------------------------------------ */}

                        {
                            connected && (

                                <button
                                    className="btn btn-outline-light"
                                    onClick={handleAdd}
                                >

                                    <i className="bi bi-plus-circle-fill me-2"></i>

                                    {t("forum.newTopic")}

                                </button>
                            )
                        }

                    </div>

                </div>

                {/* ==================================================
                    BODY
                ================================================== */}
                <div className="card-body">
                    {/* ==================================================
                        UTILISATEUR NON CONNECTÉ
                    ================================================== */}
                    {
                        !connected && (

                            <div
                                className="
                                    alert
                                    alert-warning
                                "
                            >

                                <i className="bi bi-info-circle-fill me-2"></i>

                                {t("forum.guestMessage")}

                            </div>
                        )
                    }

                    {/* ==================================================
                        RECHERCHE
                    ================================================== */}

                    <ForumSearchBar
                        keyword={keyword}
                        onSearch={(value) => {

                            setKeyword(value);

                            setPage(0);
                        }}
                        onAdd={handleAdd}
                    />

                    {/* ==================================================
                        LISTE DES SUJETS
                    ================================================== */}

                    <ForumTopicList
                        topics={topics}
                        loading={loading}
                        currentUser={loggedUser}
                        onDetail={handleDetail}
                        onEdit={handleEdit}
                        onDelete={selectId}
                    />

                    {/* ==================================================
                        PAGINATION
                    ================================================== */}

                    <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                    />

                </div>

            </div>

            {/* ========================================================
                FORMULAIRE
            ======================================================== */}

            {
                showForm && (

                    <ForumTopicForm
                        topic={selectedTopic}
                        onClose={() => {
                            setShowForm(false);
                        }}
                        onSaved={handleSaved}
                    />
                )
            }

            {/* ========================================================
                DETAIL
            ======================================================== */}

            {
                showDetail && (

                    <ForumTopicDetail
                        topic={selectedTopic}
                        onClose={() => {
                            setShowDetail(false);
                        }}
                    />
                )
            }

            {/* ========================================================
                MODAL CONFIRMATION SUPPRESSION
            ======================================================== */}

            <ConfirmationModal
                isOpen={showConfirmationModal}
                onConfirm={handleConfirmDelete}
                onCancel={handleCloseModal}
            />

        </div>
    );
}

